import { motion } from 'framer-motion';
import { Score } from '../types';
import { characteristics } from '../data/characteristics';

// Color mapping for each characteristic category
const categoryColorMap: Record<string, string> = {
  being: 'bg-teal-50',
  doing: 'bg-orange-40',
  having: 'bg-success-40',
};

interface Props {
  scores: Score[];
  compact?: boolean;
}

export function ResultsGraph({ scores, compact = false }: Props) {
  const orderedScores = characteristics.map(characteristic => {
    return scores.find(score => score.characteristicId === characteristic.id)!;
  });

  return (
    <div className="space-y-4">
      {orderedScores.map((score, index) => {
        const characteristic = characteristics[index];
        return (
          <motion.div
            key={characteristic.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="relative group"
          >
            <div className="flex items-center gap-4">
              <div className={`${compact ? 'w-24' : 'w-32'} text-right flex-shrink-0`}>
                <div className={`font-medium text-gray-900 ${compact ? 'text-sm' : 'text-base'}`}>
                  {characteristic.name}
                </div>
                {!compact && (
                  <div className="text-xs text-gray-500 capitalize">
                    {characteristic.category}
                  </div>
                )}
              </div>
              
              <div className={`flex-1 ${compact ? 'h-3' : 'h-4'} bg-gray-200 rounded-full overflow-hidden`}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${score.normalized}%` }}
                  transition={{ duration: 1, delay: index * 0.1 }}
                  className={`h-full rounded-full ${categoryColorMap[characteristic.category] || 'bg-gray-700'}`}
                />
              </div>
              
              <div className={`${compact ? 'w-10 text-sm' : 'w-12 text-base'} font-semibold text-gray-900 text-right flex-shrink-0`}>
                {Math.round(score.normalized)}
              </div>
            </div>
            
            {!compact && (
              <div className="opacity-0 group-hover:opacity-100 absolute left-1/2 -translate-x-1/2 top-full mt-2 
                            bg-white border border-gray-200 rounded-lg p-3 text-gray-700 text-sm transition-all duration-300 z-10 w-64 shadow-lg">
                {characteristic.description}
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}